import { useState } from 'react';
import type { FormEvent } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { ApiError } from '@/lib/api/client';
import { getCabinetRouteForUser } from '@/lib/roleRoutes';
import { routes } from '@/lib/routes';
import { AuthCard } from './components/AuthCard';
import { validateLogin } from './validation';
import type { LoginErrors } from './validation';

const LoginPage = () => {
  const { t } = useI18n();
  const { login, isPending } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();

  const initialEmail =
    (location.state as { email?: string } | null)?.email || params.get('email') || '';
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState<LoginErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors = validateLogin(email, password);
    setErrors(nextErrors);
    setSubmitError(null);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      const user = await login(email.trim(), password);
      notify(t('auth.signedIn', { name: user.fullName }), 'success');
      navigate(getCabinetRouteForUser(user));
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        setSubmitError(t('auth.error.invalidCredentials'));
        return;
      }
      setSubmitError(error instanceof ApiError ? error.message : t('common.error'));
    }
  };

  return (
    <AuthCard title={t('auth.login.title')} description={t('auth.login.text')} showTabs={false}>
      <form className="auth-form" onSubmit={handleSubmit} noValidate>
        <div className="auth-login-fields">
          <div className="auth-field">
            <label htmlFor="login-email">{t('auth.emailOrId')}</label>
            <input
              id="login-email"
              type="text"
              autoComplete="username"
              value={email}
              placeholder={t('auth.emailPlaceholder')}
              aria-invalid={Boolean(errors.email)}
              onChange={(event) => {
                setEmail(event.target.value);
                setSubmitError(null);
              }}
            />
            {errors.email && <span className="field-error">{t(errors.email)}</span>}
            <button type="button" className="auth-link" onClick={() => navigate(routes.forgot)}>
              {t('auth.forgotLink')}
            </button>
          </div>

          <div className="auth-field">
            <label htmlFor="login-password">{t('auth.password')}</label>
            <input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              aria-invalid={Boolean(errors.password)}
              onChange={(event) => {
                setPassword(event.target.value);
                setSubmitError(null);
              }}
            />
            {errors.password && <span className="field-error">{t(errors.password)}</span>}
          </div>
        </div>

        {submitError && (
          <p className="field-error" role="alert">
            {submitError}
          </p>
        )}

        <div className="auth-actions">
          <button type="submit" className="auth-primary" disabled={isPending}>
            {isPending ? t('common.loading') : t('auth.submitLogin')}
          </button>
          <button
            type="button"
            className="auth-secondary"
            onClick={() => navigate(routes.requestAccess)}
          >
            {t('auth.requestAccess')}
          </button>
        </div>
      </form>
    </AuthCard>
  );
};

export default LoginPage;
